import { Button, Grid } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useContext } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { AxiosError } from "axios";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import CustomLoader from "../../../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../util/state/globalVariables";
import { AncillarySubtype, draft, IGetCompanyReturn, offerDraft, RESPONSE_CHECK_CODE, RESPONSE_TYPE_SERVICE, useCheckAffiliatedQueryObserver, useCouponQueryObserver, useRatePlanQueryObserver, useTotalToBePaidQueryObserver } from "../../../../api/services";
import { LOCALE_DATE_TIME_FORMAT } from "../../../../utils";
import { accordionsExpandedState, bookingIdState, codeDescriptionState, expirationDateTimeState, idSmProductDefaultByDraftState, isBusinessContinuityMode, isLeisureCode, isOfferState, requestUpdateTimeState, stepState, isSelectedCarGroupUnavailableState } from "../../recoil";
import { BasicDetails, ExtraServices, TotalToBePaid } from "../../sections";
import { IFormValues, InsertBookingStep } from "../../types";
import { ReactQueryKey } from "../../../../types";

const AvailabilityPrice = () => {

    const navigate = useNavigate();

    const { t } = useTranslation('booking');

    const { dispatchGlobalData, globalData } = useContext(GlobalContext);

    const queryClient = useQueryClient();

    const setAccordionsExpanded = useSetRecoilState(accordionsExpandedState);
    const setStep = useSetRecoilState(stepState);
    const setBookingId = useSetRecoilState(bookingIdState);
    const idSmProductDefault = useSetRecoilState(idSmProductDefaultByDraftState);
    const setExpirationDateTime = useSetRecoilState(expirationDateTimeState);
    const setRequestUpdateTime = useSetRecoilState(requestUpdateTimeState);
    const flagLeisureCode = useRecoilValue(isLeisureCode);
    const codeDescription = useRecoilValue(codeDescriptionState);
    const businessContinuityMode = useRecoilValue(isBusinessContinuityMode);

    const formContext = useFormContext<IFormValues>();

    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealers" });

    const [isOffer, setIsOffer] = useRecoilState(isOfferState);


    const couponQuery = useCouponQueryObserver({ tenant: tenant as string, dealer: dealer as string });
    const ratePlanQuery = useRatePlanQueryObserver({ tenant: tenant as string, dealer: dealer as string });
    const totalToBePaidQuery = useTotalToBePaidQueryObserver({ tenant: tenant as string, dealer: dealer as string });
    const checkAffiliatedQuery = useCheckAffiliatedQueryObserver({ tenant: tenant as string, dealer: dealer as string });

    const isSelectedCarGroupUnavailable = useRecoilValue(isSelectedCarGroupUnavailableState);

    const draftMutation = useMutation(
        draft,
        {
            onSuccess: (data) => {
                const response = data.data;
                setBookingId(response.id);
                setAccordionsExpanded({
                    extraServices: false,
                    total: true,
                    bookingOwner: false,
                    invoice: false,
                    firstDriver: true,
                    additionalDrivers: false,
                    billingAddress: true
                });
                setExpirationDateTime(dayjs(response.expirationDateTime));
                setRequestUpdateTime(response.requestUpdateDate);
                setStep(InsertBookingStep.CUSTOMER_DETAILS);
                idSmProductDefault(response.idSmProduct);

                const additionalDriversAncillary = formContext.getValues(`extraServices.ancillary.${AncillarySubtype.AdditionalDrivers}`) as number | undefined;

                const additionalDrivers = formContext.getValues('additionalDrivers');
                if (additionalDriversAncillary) {

                    if (additionalDrivers?.length > additionalDriversAncillary) {
                        const newValueAdditionalDrivers = [];
                        for (let i = 0; i < additionalDriversAncillary; i++) {
                            newValueAdditionalDrivers.push({
                                id: '',
                                name: '',
                                surname: '',
                                email: ''
                            })
                        }
                        formContext.setValue('additionalDrivers', newValueAdditionalDrivers);
                    } else if (additionalDrivers?.length < additionalDriversAncillary) {
                        const newValueAdditionalDrivers = [];
                        for (let i = additionalDrivers?.length; i < additionalDriversAncillary; i++) {
                            newValueAdditionalDrivers.push({
                                id: '',
                                name: '',
                                surname: '',
                                email: ''
                            })
                        }
                        formContext.setValue('additionalDrivers', newValueAdditionalDrivers);
                    }
                }
                setIsOffer(false);
            },
            onError: (err) => {
                let message = '';
                if (err instanceof (AxiosError)) {
                    switch (err.response?.data.code) {
                        case '10':
                            message = t('message.error.rate_not_configured');
                            break;
                        case '50':
                            message = t('message.error.maximum_number_rentals');
                            break;
                        default:
                            message = t('message.error.default');
                    }
                }

                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: message,
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    );

    const offerMutation = useMutation( 
        // TODO: Cla -> check how the be handles the draft thingy
        offerDraft,
        {
            onSuccess: (data) => {
                const response = data.data;
                setBookingId(response.id);
                setAccordionsExpanded({
                    extraServices: false,
                    total: true,
                    bookingOwner: false,
                    invoice: false,
                    firstDriver: true,
                    additionalDrivers: false,
                    billingAddress: true
                });
                setExpirationDateTime(dayjs(response.expirationDateTime));
                setRequestUpdateTime(response.requestUpdateDate);
                setStep(InsertBookingStep.CUSTOMER_DETAILS);
                idSmProductDefault(response.idSmProduct);

                const additionalDriversAncillary = formContext.getValues(`extraServices.ancillary.${AncillarySubtype.AdditionalDrivers}`) as number | undefined;

                const additionalDrivers = formContext.getValues('additionalDrivers');
                if (additionalDriversAncillary) {

                    if (additionalDrivers?.length > additionalDriversAncillary) {
                        const newValueAdditionalDrivers = [];
                        for (let i = 0; i < additionalDriversAncillary; i++) {
                            newValueAdditionalDrivers.push({
                                id: '',
                                name: '',
                                surname: '',
                                email: ''
                            })
                        }
                        formContext.setValue('additionalDrivers', newValueAdditionalDrivers);
                    } else if (additionalDrivers?.length < additionalDriversAncillary) {
                        const newValueAdditionalDrivers = [];
                        for (let i = additionalDrivers?.length; i < additionalDriversAncillary; i++) {
                            newValueAdditionalDrivers.push({
                                id: '',
                                name: '',
                                surname: '',
                                email: ''
                            })
                        }
                        formContext.setValue('additionalDrivers', newValueAdditionalDrivers);
                    }
                }
                setIsOffer(true);
            },
            onError: (err) => {
                let message = '';
                if (err instanceof (AxiosError)) {
                    switch (err.response?.data.code) {
                        case '10':
                            message = t('message.error.rate_not_configured');
                            break;
                        case '50':
                            message = t('message.error.maximum_number_rentals');
                            break;
                        default:
                            message = t('message.error.default');
                    }
                }

                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: message,
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    );


    const handleClickConfirm = useCallback(async (data: IFormValues) => {
        if (
            !data.basicDetails.pickupLocation ||
            !data.basicDetails.dropoffLocation ||
            !data.basicDetails.carGroup ||
            !data.basicDetails.mileageIncluded ||
            !ratePlanQuery.data ||
            !totalToBePaidQuery.data
        ) {
            return;
        }

        const productAffiliatedQuery = queryClient.getQueryData<IGetCompanyReturn>([
            ReactQueryKey.Product,
            {
                segment: data.basicDetails.rentalType,
                tenant: data.basicDetails.tenants as string,
                dealer: data.basicDetails.dealers as string,
            }]);

        await draftMutation.mutateAsync({
            draftData: {
                rentalType: data.basicDetails.rentalType,
                paymentCodeDescription: productAffiliatedQuery?.paymentMethod ?? '',
                codeDescription: codeDescription,
                segment: productAffiliatedQuery?.segmentCode || '',
                customerCode: data.basicDetails.customerCode || '',
                cig: data.basicDetails.cig || '',
                pickupLocation: data.basicDetails.pickupLocation,
                dropoffLocation: data.basicDetails.dropoffLocation,
                pickupDateLocalTime: data.basicDetails.pickupDate?.format(LOCALE_DATE_TIME_FORMAT) || '',
                dropoffDateLocalTime: data.basicDetails.dropoffDate?.format(LOCALE_DATE_TIME_FORMAT) || '',
                carGroup: data.basicDetails.carGroup,
                mileageIncluded: data.basicDetails.mileageIncluded,
                ancillaries: Object
                    .entries(data.extraServices.ancillary)
                    .reduce((arr: any[], [key, value]: [string, number | boolean]) => {
                        const currAncillary = ratePlanQuery.data?.ancillaries.find(a => {
                            if (key === AncillarySubtype.AdditionalDrivers) {
                                return a.subtype === AncillarySubtype.AdditionalDrivers;
                            }
                            return a.ancillaryCode === key;
                        });
                        switch (typeof value) {
                            case 'number':
                                arr.push({
                                    ancillaryCode: currAncillary?.ancillaryCode ?? '',
                                    units: currAncillary?.mileageNeeded ? null : value,
                                    mileage: currAncillary?.mileageNeeded ? value : null,
                                    selected: value > 0,
                                    label: currAncillary?.name.defaultLabel,
                                    subtype: currAncillary?.subtype ?? 'NONE',
                                    sellType: currAncillary?.ancillaryDetails[0]?.sellType ?? '',
                                    passageToCounterNeeded: currAncillary?.passageToCounterNeeded == undefined || currAncillary?.passageToCounterNeeded == null ? false : currAncillary?.passageToCounterNeeded
                                });
                                break;
                            case 'boolean':
                                arr.push({
                                    ancillaryCode: currAncillary?.ancillaryCode ?? '',
                                    units: 1,
                                    mileage: null,
                                    selected: value,
                                    label: currAncillary?.name.defaultLabel,
                                    subtype: currAncillary?.subtype ?? 'NONE',
                                    sellType: currAncillary?.ancillaryDetails[0]?.sellType ?? '',
                                    passageToCounterNeeded: currAncillary?.passageToCounterNeeded == undefined || currAncillary?.passageToCounterNeeded == null ? false : currAncillary?.passageToCounterNeeded
                                });
                                break;
                        }
                        return arr;
                    }, []),
                collectionState: data.extraServices.collectionState,
                collectionStreetName: data.extraServices.collectionStreetName,
                collectionStreetNumber: data.extraServices.collectionStreetNumber,
                collectionPostalCode: data.extraServices.collectionPostalCode,
                collectionCity: data.extraServices.collectionCity,
                deliveryState: data.extraServices.deliveryState,
                deliveryStreetName: data.extraServices.deliveryStreetName,
                deliveryStreetNumber: data.extraServices.deliveryStreetNumber,
                deliveryPostalCode: data.extraServices.deliveryPostalCode,
                deliveryCity: data.extraServices.deliveryCity,
                discountCode: data.extraServices.discountType === 'promo' && couponQuery.data?.code === RESPONSE_CHECK_CODE.SUCCESS ?
                    data.extraServices.discountCode
                    : undefined,
                discountType: flagLeisureCode ? RESPONSE_TYPE_SERVICE.leisure : '',
                discountOverprice: data.extraServices.discountOverprice || undefined,
                note: data.extraServices.note,
                totals: [],
                dossierNumber: data.basicDetails.dossierNumber ?? '',
                reservedTariff: !!productAffiliatedQuery?.reservedTariff,
                affiliationCode: !!checkAffiliatedQuery?.data && checkAffiliatedQuery?.data.valid ? data.extraServices.affiliationCode : '',
                flightNumber: data.extraServices.flightNumber,
                totalToBePaid: 0,
                tenant: data.basicDetails.tenants as string, //globalData.user.legalEntities[0].name ?? '',
                dealer: data.basicDetails.dealers as string, //globalData.user.dealers[0].name ?? '',
                fcaBankCardNumber: data.extraServices.discountType === 'fca' && couponQuery.data?.code === RESPONSE_CHECK_CODE.SUCCESS ?
                    data.extraServices.fcaBankCardNumber :
                    undefined,
                products: ratePlanQuery.data.mileageList
                    .find(m => m.value === data.basicDetails.mileageIncluded)?.products
                    .map(p => ({
                        product: p.product,
                        rateId: p.rateId,
                        rateName: p.rateName
                    })) ?? [],
                recovery: businessContinuityMode,
                bookingDateTime: data.basicDetails.bookingDateTime?.toISOString() || null
            },
            total: totalToBePaidQuery.data
        })
    }, [
        totalToBePaidQuery.data,
        couponQuery.data,
        ratePlanQuery.data
    ]);

    const handleClickOffer = useCallback(async (data: IFormValues) => {
        if (
            !data.basicDetails.pickupLocation ||
            !data.basicDetails.dropoffLocation ||
            !data.basicDetails.carGroup ||
            !data.basicDetails.mileageIncluded ||
            !ratePlanQuery.data ||
            !totalToBePaidQuery.data
        ) {
            return;
        }

        const productAffiliatedQuery = queryClient.getQueryData<IGetCompanyReturn>([
            ReactQueryKey.Product,
            {
                segment: data.basicDetails.rentalType,
                tenant: data.basicDetails.tenants as string,
                dealer: data.basicDetails.dealers as string,
            }]);

        // TODO: Cla -> maybe change something in here
        await offerMutation.mutateAsync({
            draftData: {
                rentalType: data.basicDetails.rentalType,
                paymentCodeDescription: productAffiliatedQuery?.paymentMethod ?? '',
                codeDescription: codeDescription,
                segment: productAffiliatedQuery?.segmentCode || '',
                customerCode: data.basicDetails.customerCode || '',
                cig: data.basicDetails.cig || '',
                pickupLocation: data.basicDetails.pickupLocation,
                dropoffLocation: data.basicDetails.dropoffLocation,
                pickupDateLocalTime: data.basicDetails.pickupDate?.format(LOCALE_DATE_TIME_FORMAT) || '',
                dropoffDateLocalTime: data.basicDetails.dropoffDate?.format(LOCALE_DATE_TIME_FORMAT) || '',
                carGroup: data.basicDetails.carGroup,
                mileageIncluded: data.basicDetails.mileageIncluded,
                ancillaries: Object
                    .entries(data.extraServices.ancillary)
                    .reduce((arr: any[], [key, value]: [string, number | boolean]) => {
                        const currAncillary = ratePlanQuery.data?.ancillaries.find(a => {
                            if (key === AncillarySubtype.AdditionalDrivers) {
                                return a.subtype === AncillarySubtype.AdditionalDrivers;
                            }
                            return a.ancillaryCode === key;
                        });
                        switch (typeof value) {
                            case 'number':
                                arr.push({
                                    ancillaryCode: currAncillary?.ancillaryCode ?? '',
                                    units: currAncillary?.mileageNeeded ? null : value,
                                    mileage: currAncillary?.mileageNeeded ? value : null,
                                    selected: value > 0,
                                    label: currAncillary?.name.defaultLabel,
                                    subtype: currAncillary?.subtype ?? 'NONE',
                                    sellType: currAncillary?.ancillaryDetails[0]?.sellType ?? '',
                                    passageToCounterNeeded: currAncillary?.passageToCounterNeeded == undefined || currAncillary?.passageToCounterNeeded == null ? false : currAncillary?.passageToCounterNeeded
                                });
                                break;
                            case 'boolean':
                                arr.push({
                                    ancillaryCode: currAncillary?.ancillaryCode ?? '',
                                    units: 1,
                                    mileage: null,
                                    selected: value,
                                    label: currAncillary?.name.defaultLabel,
                                    subtype: currAncillary?.subtype ?? 'NONE',
                                    sellType: currAncillary?.ancillaryDetails[0]?.sellType ?? '',
                                    passageToCounterNeeded: currAncillary?.passageToCounterNeeded == undefined || currAncillary?.passageToCounterNeeded == null ? false : currAncillary?.passageToCounterNeeded
                                });
                                break;
                        }
                        return arr;
                    }, []),
                collectionState: data.extraServices.collectionState,
                collectionStreetName: data.extraServices.collectionStreetName,
                collectionStreetNumber: data.extraServices.collectionStreetNumber,
                collectionPostalCode: data.extraServices.collectionPostalCode,
                collectionCity: data.extraServices.collectionCity,
                deliveryState: data.extraServices.deliveryState,
                deliveryStreetName: data.extraServices.deliveryStreetName,
                deliveryStreetNumber: data.extraServices.deliveryStreetNumber,
                deliveryPostalCode: data.extraServices.deliveryPostalCode,
                deliveryCity: data.extraServices.deliveryCity,
                discountCode: data.extraServices.discountType === 'promo' && couponQuery.data?.code === RESPONSE_CHECK_CODE.SUCCESS ?
                    data.extraServices.discountCode
                    : undefined,
                discountType: flagLeisureCode ? RESPONSE_TYPE_SERVICE.leisure : '',
                discountOverprice: data.extraServices.discountOverprice || undefined,
                note: data.extraServices.note,
                totals: [],
                dossierNumber: data.basicDetails.dossierNumber ?? '',
                reservedTariff: !!productAffiliatedQuery?.reservedTariff,
                affiliationCode: !!checkAffiliatedQuery?.data && checkAffiliatedQuery?.data.valid ? data.extraServices.affiliationCode : '',
                flightNumber: data.extraServices.flightNumber,
                totalToBePaid: 0,
                tenant: data.basicDetails.tenants as string, //globalData.user.legalEntities[0].name ?? '',
                dealer: data.basicDetails.dealers as string, //globalData.user.dealers[0].name ?? '',
                fcaBankCardNumber: data.extraServices.discountType === 'fca' && couponQuery.data?.code === RESPONSE_CHECK_CODE.SUCCESS ?
                    data.extraServices.fcaBankCardNumber :
                    undefined,
                products: ratePlanQuery.data.mileageList
                    .find(m => m.value === data.basicDetails.mileageIncluded)?.products
                    .map(p => ({
                        product: p.product,
                        rateId: p.rateId,
                        rateName: p.rateName
                    })) ?? [],
                recovery: businessContinuityMode,
                bookingDateTime: data.basicDetails.bookingDateTime?.toISOString() || null
            },
            total: totalToBePaidQuery.data
        })
    }, [
        totalToBePaidQuery.data,
        couponQuery.data,
        ratePlanQuery.data
    ]);

    const handleReset = useCallback(() => {
        window.history.replaceState({}, document.title);
        navigate(0);
    }, []);

    return (
        <Grid container spacing={2} alignItems="start">
            <Grid item xs={12} sm={12} md={6}>
                <BasicDetails />
            </Grid>
            <Grid item container xs={12} sm={12} md={6} spacing={2}>
                <Grid item xs={12}>
                    <ExtraServices />
                </Grid>
                <Grid item xs={12}>
                    <TotalToBePaid />
                </Grid>
                <Grid container item xs={12} justifyContent="end" columnGap={2}>
                    <Button
                        variant="outlined"
                        onClick={handleReset}
                    >
                        {t('label.reset')}
                    </Button>
                    <Button variant="outlined"
                        onClick={formContext.handleSubmit(handleClickOffer)}
                        disabled={
                            ratePlanQuery.isFetching ||
                            totalToBePaidQuery.isFetching ||
                            isSelectedCarGroupUnavailable
                        }
                    >
                        {t('label.create_offer')}
                    </Button>
                    <Button
                        onClick={formContext.handleSubmit(handleClickConfirm)}
                        disabled={
                            ratePlanQuery.isFetching ||
                            totalToBePaidQuery.isFetching ||
                            isSelectedCarGroupUnavailable
                        }
                    >
                        {t('label.confirm')}
                    </Button>
                </Grid>
            </Grid>
            <CustomLoader loading={draftMutation.isLoading} />
        </Grid>
    );
};

export default AvailabilityPrice;
